import { useMemo, useState } from "react";

import { FileText, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  useCreateMedicalTemplate,
  useDeleteMedicalTemplate,
  useMedicalTemplates,
  useUpdateMedicalTemplate,
} from "@/features/medicalTemplates/hooks/useMedicalTemplates";
import type { MedicalTemplate } from "@/features/medicalTemplates/schemas/medicalTemplate";

type EditorMode = "idle" | "create" | "edit";

type FormState = {
  name: string;
  description: string;
  category: string;
  formFields: string;
  content: string;
  prompt: string;
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  category: "",
  formFields: "",
  content: "",
  prompt: "",
  isActive: true,
};

function groupTemplatesByCategory(
  templates: MedicalTemplate[]
): Record<string, MedicalTemplate[]> {
  const groups: Record<string, MedicalTemplate[]> = {};
  for (const tpl of templates) {
    if (!groups[tpl.category]) groups[tpl.category] = [];
    groups[tpl.category].push(tpl);
  }
  return groups;
}

/**
 * CRUD de plantillas de historia médica (colección Firestore `medical_templates`).
 */
export function MedicalTemplatesEditor() {
  const { currentUser } = useAuth();
  const { data: templates = [], isLoading } = useMedicalTemplates();
  const createTemplate = useCreateMedicalTemplate();
  const updateTemplate = useUpdateMedicalTemplate();
  const deleteTemplate = useDeleteMedicalTemplate();

  const [mode, setMode] = useState<EditorMode>("idle");
  const [editing, setEditing] = useState<MedicalTemplate | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const groupedTemplates = useMemo(
    () => groupTemplatesByCategory(templates),
    [templates]
  );

  const categorySuggestions = useMemo(() => {
    return Array.from(new Set(templates.map((tpl) => tpl.category))).sort(
      (a, b) => a.localeCompare(b, "es")
    );
  }, [templates]);

  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  const resetEditor = () => {
    setMode("idle");
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const startCreate = () => {
    setMode("create");
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (tpl: MedicalTemplate) => {
    setMode("edit");
    setEditing(tpl);
    setForm({
      name: tpl.name,
      description: tpl.description ?? "",
      category: tpl.category,
      formFields: tpl.formFields ?? "",
      content: tpl.content ?? "",
      prompt: tpl.prompt,
      isActive: tpl.isActive,
    });
  };

  const patchForm = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.category.trim() || !form.prompt.trim()) {
      toast.error("Nombre, categoría y prompt son obligatorios");
      return;
    }

    const payload = {
      name: form.name,
      description: form.description,
      category: form.category,
      formFields: form.formFields,
      content: form.content,
      prompt: form.prompt,
      isActive: form.isActive,
    };

    try {
      if (mode === "create") {
        if (!currentUser) {
          toast.error("No hay usuario autenticado");
          return;
        }
        await createTemplate.mutateAsync({
          input: payload,
          authorUid: currentUser.uid,
        });
        toast.success(`Plantilla "${form.name}" creada`);
      } else if (mode === "edit" && editing) {
        await updateTemplate.mutateAsync({
          docId: editing.docId,
          data: payload,
        });
        toast.success(`Plantilla "${form.name}" actualizada`);
      }
      resetEditor();
    } catch (error) {
      console.error("Error guardando plantilla:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo guardar la plantilla"
      );
    }
  };

  const handleDelete = async (tpl: MedicalTemplate) => {
    const ok = window.confirm(`¿Eliminar la plantilla "${tpl.name}"?`);
    if (!ok) return;

    try {
      await deleteTemplate.mutateAsync(tpl.docId);
      toast.success(`Plantilla "${tpl.name}" eliminada`);
      if (editing?.docId === tpl.docId) resetEditor();
    } catch (error) {
      console.error("Error eliminando plantilla:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo eliminar la plantilla"
      );
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" aria-hidden />
          Plantillas de historia médica
        </CardTitle>
        <CardDescription>
          Catálogo Firestore <code className="text-xs">medical_templates</code>{" "}
          — solo plantillas <code className="text-xs">aiTemplate: true</code>,
          gestionadas desde la plataforma.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {templates.length} plantilla(s)
          </p>
          <Button
            size="sm"
            className="cursor-pointer shrink-0"
            onClick={startCreate}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-1.5" aria-hidden />
            Nueva plantilla
          </Button>
        </div>

        {isLoading ? (
          <div
            className="flex justify-center py-8"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="sr-only">Cargando plantillas</span>
          </div>
        ) : templates.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-6">
            No hay plantillas. Agregá la primera.
          </p>
        ) : (
          <div className="max-h-[min(28rem,60vh)] space-y-2 overflow-y-auto rounded-md border p-2">
            {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
              <div key={category}>
                <h3 className="mb-1 px-1 text-xs font-medium text-muted-foreground">
                  {category} ({categoryTemplates.length})
                </h3>
                <ul className="space-y-1">
                  {categoryTemplates.map((tpl) => (
                    <li
                      key={tpl.docId}
                      className="flex items-start justify-between gap-2 rounded-md border px-2 py-1.5 transition-colors hover:bg-muted/40"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-sm font-medium leading-tight">
                            {tpl.name}
                          </p>
                          <Badge variant={tpl.isActive ? "default" : "secondary"}>
                            {tpl.isActive ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                        {tpl.description && (
                          <p className="truncate text-[11px] text-muted-foreground">
                            {tpl.description}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-9 cursor-pointer"
                          aria-label={`Editar ${tpl.name}`}
                          onClick={() => startEdit(tpl)}
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-9 cursor-pointer"
                          aria-label={`Eliminar ${tpl.name}`}
                          onClick={() => void handleDelete(tpl)}
                          disabled={deleteTemplate.isPending}
                        >
                          <Trash2
                            className="h-3.5 w-3.5 text-destructive"
                            aria-hidden
                          />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog
        open={mode === "create" || mode === "edit"}
        onOpenChange={(isOpen) => !isOpen && resetEditor()}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto gap-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
              {mode === "create" ? "Nueva plantilla" : `Editar: ${editing?.name}`}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Modelo de historia clínica, opcionalmente con prompt de IA.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="mt-name">Nombre</Label>
                <Input
                  id="mt-name"
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value })}
                  placeholder="SOAP Adultos"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mt-category">Categoría</Label>
                <Input
                  id="mt-category"
                  value={form.category}
                  onChange={(e) => patchForm({ category: e.target.value })}
                  placeholder="Ej: history"
                  list="mt-category-suggestions"
                />
                <datalist id="mt-category-suggestions">
                  {categorySuggestions.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="mt-description">Descripción (opcional)</Label>
                <Textarea
                  id="mt-description"
                  value={form.description}
                  onChange={(e) => patchForm({ description: e.target.value })}
                  rows={2}
                  placeholder="Modelo de historia SOAP de adultos"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="mt-form-fields">Campos del formulario (opcional)</Label>
                <Textarea
                  id="mt-form-fields"
                  value={form.formFields}
                  onChange={(e) => patchForm({ formFields: e.target.value })}
                  rows={2}
                  placeholder="SUBJETIVO OBJETIVO ANALISIS PLAN"
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="mt-content">Contenido (opcional)</Label>
                <Textarea
                  id="mt-content"
                  value={form.content}
                  onChange={(e) => patchForm({ content: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="mt-prompt">Prompt</Label>
                <Textarea
                  id="mt-prompt"
                  value={form.prompt}
                  onChange={(e) => patchForm({ prompt: e.target.value })}
                  rows={12}
                  className="font-mono text-xs"
                  placeholder="Rol, tarea, instrucciones detalladas para la IA…"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(checked) => patchForm({ isActive: checked })}
                  id="mt-active"
                />
                <Label htmlFor="mt-active">Activa</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={resetEditor}
                disabled={isSaving}
              >
                <X className="h-4 w-4 mr-1.5" aria-hidden />
                Cancelar
              </Button>
              <Button
                size="sm"
                className="cursor-pointer"
                onClick={() => void handleSave()}
                disabled={isSaving}
              >
                {isSaving && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                )}
                {mode === "create" ? "Crear plantilla" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
